// <s-scene-slider> — centered, looping scene slider. The active slide sits in the middle with
// neighbours peeking in at both sides (scaled down), round arrows over the image edges, and
// pagination dots where the active dot stretches into a pill. Native scroll-snap does the
// swiping. To loop, one copy of the slides is placed before and after the real ones; when
// scrolling settles on a copy, the track jumps (without animation) to the matching real slide.
class SSceneSlider extends HTMLElement {
  #ac = null;
  #track = null;
  #slides = [];
  #count = 0;
  #dots = [];
  #active = -1;
  #frame = 0;
  #settleTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#track = this.querySelector("[data-scene-track]");
    if (!this.#track) return;

    const real = [...this.#track.querySelectorAll("[data-scene-slide]")];
    this.#count = real.length;
    this.#dots = [...this.querySelectorAll("[data-scene-dot]")];
    if (this.#count > 1) {
      const makeClone = (slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute("data-scene-clone", "");
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("role");
        clone.removeAttribute("aria-roledescription");
        clone.querySelectorAll("img").forEach((img) => img.setAttribute("alt", ""));
        return clone;
      };
      const before = real.map(makeClone);
      const after = real.map(makeClone);
      real[0].before(...before);
      real[real.length - 1].after(...after);
    }
    this.#slides = [...this.#track.querySelectorAll("[data-scene-slide]")];

    this.querySelector("[data-scene-prev]")?.addEventListener("click", () => this.#step(-1), { signal });
    this.querySelector("[data-scene-next]")?.addEventListener("click", () => this.#step(1), { signal });
    this.#dots.forEach((dot, i) => dot.addEventListener("click", () => this.#goTo(this.#offset() + i, true), { signal }));
    // Clicking a side slide brings it to the centre; clicking the centre slide moves to the next one.
    this.#slides.forEach((slide, i) =>
      slide.addEventListener("click", () => (i === this.#active ? this.#step(1) : this.#goTo(i, true)), { signal })
    );
    this.#track.addEventListener("scroll", () => this.#onScroll(), { passive: true, signal });
    this.#track.addEventListener("scrollend", () => this.#settle(), { signal });
    window.addEventListener("resize", () => this.#goTo(this.#active < 0 ? this.#offset() : this.#active, false), { passive: true, signal });
    this.#track.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "ArrowRight") { e.preventDefault(); this.#step(1); }
        if (e.key === "ArrowLeft") { e.preventDefault(); this.#step(-1); }
      },
      { signal }
    );

    // Start on the first real slide, centered, with a copy of the last slide peeking on the left.
    this.#goTo(this.#offset(), false);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#settleTimer);
  }

  // Index of the first real slide (after the leading copies).
  #offset() {
    return this.#count > 1 ? this.#count : 0;
  }

  #onScroll() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
    // Fallback for browsers without the scrollend event.
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#settle(), 160);
  }

  // The active slide is the one whose centre is closest to the track's centre.
  #update() {
    const trackRect = this.#track.getBoundingClientRect();
    const centre = trackRect.left + trackRect.width / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.#slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - centre);
      if (distance < bestDistance) { bestDistance = distance; best = i; }
    });
    if (best === this.#active) return;
    this.#active = best;
    this.#slides.forEach((slide, i) => slide.toggleAttribute("data-active", i === best));
    const realIndex = this.#count ? best % this.#count : 0;
    this.#dots.forEach((dot, i) => dot.setAttribute("aria-current", i === realIndex ? "true" : "false"));
  }

  // When scrolling stops on a copy, jump to the matching real slide without animation.
  #settle() {
    clearTimeout(this.#settleTimer);
    if (this.#count < 2) return;
    this.#update();
    if (this.#active < this.#count) this.#goTo(this.#active + this.#count, false);
    else if (this.#active >= this.#count * 2) this.#goTo(this.#active - this.#count, false);
  }

  #step(direction) {
    this.#settle();
    this.#goTo(this.#active + direction, true);
  }

  #goTo(index, animate) {
    const target = this.#slides[Math.max(0, Math.min(index, this.#slides.length - 1))];
    if (!target) return;
    const left = target.offsetLeft + target.offsetWidth / 2 - this.#track.clientWidth / 2;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (animate && !reduce) {
      this.#track.scrollTo({ left, behavior: "smooth" });
      return;
    }
    this.#track.setAttribute("data-jumping", "");
    this.#track.style.scrollBehavior = "auto";
    this.#track.scrollLeft = left;
    this.#active = -1;
    this.#update();
    requestAnimationFrame(() => {
      this.#track.style.scrollBehavior = "";
      this.#track.removeAttribute("data-jumping");
    });
  }
}

if (!customElements.get("s-scene-slider")) customElements.define("s-scene-slider", SSceneSlider);
